
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useCategories } from '@/hooks/useCategories';

interface SmartCategorySelectorProps {
  value: string;
  onChange: (value: string) => void;
  currentCategoryId?: string;
  required?: boolean;
}

export const SmartCategorySelector = ({ 
  value, 
  onChange, 
  currentCategoryId,
  required = false 
}: SmartCategorySelectorProps) => {
  // Get both Level 2 (states/provinces) and Level 3 categories (topics allowed in both)
  const { data: level2Categories, isLoading: level2Loading } = useCategories(undefined, 2);
  const { data: level3Categories, isLoading: level3Loading } = useCategories(undefined, 3);

  const isLoading = level2Loading || level3Loading;

  // Group Level 3 categories by their parent
  const categoriesByParent = React.useMemo(() => {
    const groups: Record<string, typeof level3Categories> = {};
    level3Categories?.forEach((cat) => {
      if (cat.parent_category_id) {
        if (!groups[cat.parent_category_id]) {
          groups[cat.parent_category_id] = [];
        }
        groups[cat.parent_category_id].push(cat);
      }
    });
    return groups;
  }, [level3Categories]);

  return (
    <div className="space-y-2">
      <Label htmlFor="category">Category</Label>
      <Select value={value} onValueChange={onChange} required={required}>
        <SelectTrigger>
          <SelectValue placeholder="Select a category for your topic" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {isLoading ? (
            <SelectItem value="loading" disabled>Loading categories...</SelectItem>
          ) : (
            <>
              {/* Level 2 categories (can post directly here) */}
              {level2Categories?.map((category) => (
                <React.Fragment key={category.id}>
                  <SelectItem value={category.id}>
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="font-medium">{category.name}</span>
                    </div>
                  </SelectItem>
                  {/* Show Level 3 subcategories under their parent */}
                  {categoriesByParent[category.id]?.map((subcat) => (
                    <SelectItem key={subcat.id} value={subcat.id}>
                      <div className="flex items-center space-x-2 pl-4">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: subcat.color }}
                        />
                        <span className="text-sm">↳ {subcat.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
              
              {/* Orphaned Level 3 categories (no parent match) */}
              {level3Categories?.filter(cat => !cat.parent_category_id || !level2Categories?.find(l2 => l2.id === cat.parent_category_id)).map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: category.color }}
                    />
                    <span>{category.name}</span>
                  </div>
                </SelectItem>
              ))}
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
